import { useState } from "react";
import { ArrowUpRight, TrendingUp, Clock, Briefcase, Sparkles, Users } from "lucide-react";

const PROFESSIONAL_STUDIES = [
  {
    id: "cs-1",
    sector: "Marketing",
    name: "Sarah K.",
    initial: "S",
    accent: "#F39319",
    headline: "From stuck on LinkedIn to Head of Marketing at a FTSE 250 firm.",
    before:
      "Sarah had been applying for 6+ months with zero interview invitations despite a strong track record.",
    after:
      "After her CV and LinkedIn makeover, she received 5 interview invitations in the first 3 weeks and accepted a Head-of-Marketing role with a £18k uplift.",
    metrics: [
      { icon: Clock, label: "First interview", value: "11 days" },
      { icon: TrendingUp, label: "Salary uplift", value: "+£18k" },
      { icon: Briefcase, label: "Offers received", value: "3" },
    ],
    quote:
      "I was applying for months and getting nothing. After CV Folks, my phone wouldn't stop ringing.",
  },
  {
    id: "cs-2",
    sector: "Healthcare → Pharma",
    name: "Dr. Tom R.",
    initial: "T",
    accent: "#1976D2",
    headline: "Pivoted from NHS clinical work into a global pharma career.",
    before:
      "Tom wanted to move from frontline NHS into industry but his clinical CV was being filtered out by every pharma application.",
    after:
      "We rewrote his CV to translate clinical expertise into industry language. He interviewed at 3 of the UK's top 5 pharma companies in 6 weeks and accepted a Medical Affairs role.",
    metrics: [
      { icon: Clock, label: "First interview", value: "18 days" },
      { icon: TrendingUp, label: "Career pivot", value: "Achieved" },
      { icon: Briefcase, label: "Offers received", value: "2" },
    ],
    quote:
      "I thought leaving the NHS was impossible with my CV. The new version made me sound like I belonged in industry.",
  },
  {
    id: "cs-3",
    sector: "Technology",
    name: "Priya R.",
    initial: "P",
    accent: "#0F9D58",
    headline: "Senior Software Engineer offer at a Series B fintech.",
    before:
      "Priya was targeting senior engineering roles but her CV was getting filtered out by ATS systems before a human ever saw it.",
    after:
      "ATS-optimised rewrite + LinkedIn overhaul. She received 8 recruiter messages and accepted a senior fintech role with a £25k uplift.",
    metrics: [
      { icon: Clock, label: "Inbound messages", value: "8 in 30 days" },
      { icon: TrendingUp, label: "Salary uplift", value: "+£25k" },
      { icon: Briefcase, label: "Offers received", value: "2" },
    ],
    quote:
      "It went from radio silence to recruiters competing for me — same person, totally different CV.",
  },
  {
    id: "cs-4",
    sector: "Finance",
    name: "James M.",
    initial: "J",
    accent: "#DB4437",
    headline: "First Investment Banking Analyst role, secured within 60 days.",
    before:
      "James had graduated 8 months earlier and was struggling to break into a top-tier investment bank.",
    after:
      "We restructured his CV around quantified impact and tailored it for IB recruiters. He landed an Analyst offer at a top-tier bank within his 60-day guarantee window.",
    metrics: [
      { icon: Clock, label: "Time to offer", value: "47 days" },
      { icon: TrendingUp, label: "Career start", value: "Top-tier IB" },
      { icon: Briefcase, label: "Interviews", value: "5" },
    ],
    quote:
      "I'd given up on banking. CV Folks made me look like a candidate, not a fresh grad.",
  },
];

const AI_STUDIES = [
  {
    id: "ai-cs-1",
    sector: "Graduate · Retail Management",
    name: "Aisha B.",
    initial: "A",
    accent: "#6B4EFF",
    headline: "£9.99 AI CV landed a Retail Manager role three weeks after graduation.",
    before:
      "Aisha had a shortlist of retail roles but her graduate CV was generic — no quantified achievements, no ATS keywords.",
    after:
      "The AI CV Builder pulled her placement work into achievement-led bullets. She uploaded the ATS-friendly PDF the same evening and secured a Retail Manager role within 21 days.",
    metrics: [
      { icon: Clock, label: "Time to offer", value: "21 days" },
      { icon: TrendingUp, label: "Cost", value: "£9.99" },
      { icon: Briefcase, label: "Applications sent", value: "12" },
    ],
    quote:
      "I couldn't afford a full CV writing service — £9.99 got me a proper CV that actually got read.",
  },
  {
    id: "ai-cs-2",
    sector: "Career Change · Ops → Product",
    name: "Marcus L.",
    initial: "M",
    accent: "#F39319",
    headline: "Used the AI Builder to reframe ops experience for a Product role.",
    before:
      "Marcus wanted to pivot into product management but didn't know how to reposition 5 years of ops work.",
    after:
      "He answered the AI wizard for his target role and let it rewrite his experience with product-relevant framing. Started interviews within 10 days and later upgraded to a Premium writer using his £9.99 credit.",
    metrics: [
      { icon: Clock, label: "First interview", value: "10 days" },
      { icon: TrendingUp, label: "Career pivot", value: "Ops → Product" },
      { icon: Briefcase, label: "Upgrade", value: "Used £9.99 credit" },
    ],
    quote:
      "The AI Builder gave me a first draft I never could've written alone. The credit toward the Premium package sealed it.",
  },
  {
    id: "ai-cs-3",
    sector: "NHS · Nurse",
    name: "Ellie P.",
    initial: "E",
    accent: "#0F9D58",
    headline: "AI-generated CV and cover letter — first interview in 8 days.",
    before:
      "Ellie hadn't updated her CV in 6 years. She needed something quickly for a Band 6 NHS role that closed within a week.",
    after:
      "She imported her old CV, the AI filled in the wizard, tweaked two bullets, downloaded the PDF plus cover letter and applied. Interview invite arrived 8 days later.",
    metrics: [
      { icon: Clock, label: "First interview", value: "8 days" },
      { icon: TrendingUp, label: "Turnaround", value: "Same day" },
      { icon: Briefcase, label: "Cover letter", value: "Included" },
    ],
    quote:
      "I had a proper NHS-ready CV and cover letter in under an hour — for less than my morning coffee for a week.",
  },
];

function Card({ c }) {
  return (
    <article
      data-testid={`case-study-${c.id}`}
      className="group relative rounded-3xl border border-border bg-card p-7 md:p-9 hover:border-foreground/30 hover:shadow-xl transition"
    >
      <div className="flex items-center justify-between gap-3 mb-5">
        <span
          className="text-[11px] uppercase tracking-[0.22em] font-bold px-3 py-1.5 rounded-full"
          style={{
            backgroundColor: `${c.accent}15`,
            color: c.accent,
          }}
        >
          {c.sector}
        </span>
        <ArrowUpRight className="h-5 w-5 text-foreground/30 group-hover:text-brand group-hover:rotate-12 transition" />
      </div>

      <h3 className="font-serif text-2xl md:text-[28px] font-semibold leading-snug">
        {c.headline}
      </h3>

      <div className="mt-5 space-y-3 text-sm text-foreground/75 leading-relaxed">
        <p>
          <span className="font-semibold text-foreground/90">Before: </span>
          {c.before}
        </p>
        <p>
          <span className="font-semibold text-foreground/90">After: </span>
          {c.after}
        </p>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3 pt-6 border-t border-border">
        {c.metrics.map((m, i) => (
          <div key={i}>
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <m.icon className="h-3.5 w-3.5" />
              <span className="text-[10px] uppercase tracking-widest font-bold">
                {m.label}
              </span>
            </div>
            <p className="font-serif text-xl font-bold mt-1">{m.value}</p>
          </div>
        ))}
      </div>

      <figure className="mt-6 pt-6 border-t border-border flex items-start gap-4">
        <span
          className="h-11 w-11 rounded-full flex items-center justify-center font-semibold text-white shrink-0"
          style={{ backgroundColor: c.accent }}
        >
          {c.initial}
        </span>
        <div>
          <blockquote className="text-sm italic text-foreground/85 leading-relaxed">
            “{c.quote}”
          </blockquote>
          <figcaption className="mt-2 text-xs text-muted-foreground font-medium">
            — {c.name}, CV Folks client
          </figcaption>
        </div>
      </figure>
    </article>
  );
}

export default function CaseStudies() {
  const [tab, setTab] = useState("professional");
  const items = tab === "ai" ? AI_STUDIES : PROFESSIONAL_STUDIES;

  return (
    <section
      id="case-studies"
      data-testid="case-studies-section"
      className="py-24 md:py-32 bg-background"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="label-eyebrow">Case Studies</p>
            <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-[1.05] max-w-3xl">
              How professionals like you turned their career around.
            </h2>
          </div>
          <p className="text-foreground/70 max-w-md">
            Real outcomes from both routes — the £9.99 AI CV Builder and our
            Professional Writing service.
          </p>
        </div>

        {/* Tabs */}
        <div
          data-testid="case-studies-tabs"
          className="inline-flex items-center gap-1 bg-muted/60 border border-border rounded-full p-1 mb-10"
          role="tablist"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === "professional"}
            data-testid="case-studies-tab-professional"
            onClick={() => setTab("professional")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition ${
              tab === "professional"
                ? "bg-foreground text-background shadow"
                : "text-foreground/70 hover:text-foreground"
            }`}
          >
            <Users className="h-4 w-4" />
            Professional CV Writing
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "ai"}
            data-testid="case-studies-tab-ai"
            onClick={() => setTab("ai")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition ${
              tab === "ai"
                ? "bg-foreground text-background shadow"
                : "text-foreground/70 hover:text-foreground"
            }`}
          >
            <Sparkles className="h-4 w-4" />
            AI CV Builder — £9.99
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {items.map((c) => (
            <Card key={c.id} c={c} />
          ))}
        </div>
      </div>
    </section>
  );
}
